import { View } from 'react-native'
import Typography from '@ca/ui/typography'
import {
  HSA_PHASE_DETAIL,
  PHASE_COLOR,
  PHASE_DETAIL,
  PHASE_PROMPT,
  hsaPromptForDebug,
  shDetailForCapture,
  shPromptForCapture,
} from './constants'
import { ProgressBar } from './ProgressBar'
import {
  useFreezeStatus,
  useHsaFaceData,
  usePoseAngles,
  useShFaceData,
} from './faceDataStore'
import type { TabId } from './constants'
import type { Phase } from './types'
import { fmt } from './utils'

type Props = {
  activeTabId: TabId
  phase: Phase | null
  progress: number
  containerHeight: number
}

function PoseAngles({
  rotationX,
  rotationY,
}: {
  rotationX: number
  rotationY: number
}) {
  return (
    <Typography variant="XS" className="text-center text-white/70">
      {`rotX ${fmt(rotationX)}°  rotY ${fmt(rotationY)}°`}
    </Typography>
  )
}

function LivePoseAngles() {
  const angles = usePoseAngles()
  if (!angles) return null

  return <PoseAngles rotationX={angles.rotationX} rotationY={angles.rotationY} />
}

function HSAPoseAngles() {
  const hsaData = useHsaFaceData()
  const debug = hsaData?.hsaDebug
  if (debug?.rotationX == null || debug.rotationY == null) return <LivePoseAngles />

  return <PoseAngles rotationX={debug.rotationX} rotationY={debug.rotationY} />
}

export function CameraStatusOverlay({
  activeTabId,
  phase,
  progress,
  containerHeight,
}: Props) {
  const freezeStatus = useFreezeStatus()
  const hsaData = useHsaFaceData()
  const shData = useShFaceData()
  const isProcessingFinalFrame =
    activeTabId === 'sh' &&
    phase === 'complete' &&
    freezeStatus.active &&
    freezeStatus.mode === 'sh' &&
    freezeStatus.processing
  const isVisible =
    isProcessingFinalFrame ||
    (phase !== null && phase !== 'idle' && phase !== 'complete')

  if (!isVisible) return null

  let overlayColor = 'bg-blue-400'
  let overlayPrompt = 'Processing Final Frame…'
  let overlayDetail: string | undefined = 'Running final model'
  if (!isProcessingFinalFrame && phase) {
    overlayColor = PHASE_COLOR[phase]
    overlayPrompt = PHASE_PROMPT[phase]
    overlayDetail = PHASE_DETAIL[phase]
    if (activeTabId === 'hsa') {
      overlayPrompt = hsaPromptForDebug(phase, hsaData?.hsaDebug)
      overlayDetail = HSA_PHASE_DETAIL[phase]
    }
    if (activeTabId === 'sh') {
      overlayPrompt = shPromptForCapture(phase, shData?.shCapture)
      overlayDetail = shDetailForCapture(phase, shData?.shCapture)
    }
  }
  const showProgress =
    isProcessingFinalFrame ||
    (phase != null &&
      (activeTabId === 'sh' || activeTabId === 'hsa' || phase === 'sampling'))

  return (
    <View
      className="absolute left-0 right-0 items-center px-6"
      style={{ top: containerHeight * 0.25 }}
      pointerEvents="none"
    >
      <View className="items-center gap-2 rounded-2xl bg-black/50 px-6 py-3">
        <View className="flex-row items-center gap-2">
          <View className={`h-3 w-3 rounded-full ${overlayColor}`} />
          <Typography variant="SMB" className="text-center text-white">
            {overlayPrompt}
          </Typography>
        </View>
        {(phase === 'positioning' || phase === 'stabilising') &&
          !isProcessingFinalFrame &&
          (activeTabId === 'hsa' ? <HSAPoseAngles /> : <LivePoseAngles />)}
        {overlayDetail && (
          <Typography variant="XS" className="text-center text-white/70">
            {overlayDetail}
          </Typography>
        )}
        {showProgress && (
          <ProgressBar
            progress={isProcessingFinalFrame ? 1 : progress}
            isPulsing={isProcessingFinalFrame}
            className="mt-1 h-2 w-48 overflow-hidden rounded-full bg-white/20"
          />
        )}
      </View>
    </View>
  )
}
